import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { User, Building, Mail, Phone, Save, Loader, Loader2, Camera, Upload, Lock, Eye, EyeOff, CheckCircle } from 'lucide-react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { api, useAuth } from '../lib/auth';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SettingsPage() {
  const { user, refreshUser } = useAuth();
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [orgName, setOrgName] = useState('');
  const [userName, setUserName] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const fileInputRef = useRef(null);
  const token = localStorage.getItem('token');

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (user?.name) {
      setUserName(user.name);
    }
  }, [user]);

  const loadData = async () => {
    try {
      const response = await api.getMyOrganization();
      setOrganization(response.data);
      setOrgName(response.data.name);
    } catch (error) {
      console.error('Failed to load organization:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateName = async () => {
    if (!userName.trim() || userName.trim().length < 2) {
      toast.error('Name must be at least 2 characters');
      return;
    }

    setSavingName(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/update-profile`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ name: userName.trim() })
      });

      if (response.ok) {
        toast.success('Name updated successfully!');
        if (refreshUser) {
          await refreshUser();
        }
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to update name');
      }
    } catch (error) {
      console.error('Failed to update name:', error);
      toast.error('Failed to update name');
    } finally {
      setSavingName(false);
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    setUploadingAvatar(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API_URL}/api/auth/upload-avatar`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        toast.success('Profile picture updated!');
        // Refresh user data to get new avatar
        if (refreshUser) {
          await refreshUser();
        }
        // Force page refresh to update avatar everywhere
        window.location.reload();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to upload image');
      }
    } catch (error) {
      console.error('Avatar upload error:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploadingAvatar(false);
      e.target.value = '';
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.updateMyOrganization(orgName);
      toast.success('Settings saved!');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto px-4 py-8 pt-10">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="font-heading text-3xl font-bold text-white mb-2">Settings</h1>
          <p className="text-gray-400">Manage your profile and organization</p>
        </motion.div>

        {/* Profile Picture Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="glass rounded-xl p-6 mb-6"
        >
          <h2 className="font-heading text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Camera className="w-5 h-5" />
            Profile Picture
          </h2>

          <div className="flex items-center gap-6">
            {/* Avatar Preview */}
            <div className="relative group">
              {user?.avatar ? (
                <img 
                  src={user.avatar} 
                  alt={user.name}
                  className="w-24 h-24 rounded-full object-cover border-4 border-white/10"
                />
              ) : (
                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-primary to-cyan-500 flex items-center justify-center border-4 border-white/10">
                  <span className="text-3xl font-bold text-white">
                    {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                  </span>
                </div>
              )}
              
              {/* Upload Overlay */}
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingAvatar}
                className="absolute inset-0 rounded-full bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer"
              >
                {uploadingAvatar ? (
                  <Loader className="w-6 h-6 text-white animate-spin" />
                ) : (
                  <Camera className="w-6 h-6 text-white" />
                )}
              </button>
            </div>

            <div className="flex-1">
              <p className="text-white font-medium mb-1">{user?.name}</p>
              <p className="text-sm text-gray-400 mb-3">{user?.email}</p>
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingAvatar}
                variant="outline"
                className="border-white/20 text-white hover:bg-white/10"
                data-testid="upload-avatar-btn"
              >
                {uploadingAvatar ? (
                  <>
                    <Loader className="w-4 h-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Upload Photo
                  </>
                )}
              </Button>
              <p className="text-xs text-gray-500 mt-2">JPG, PNG or GIF. Max 5MB.</p>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleAvatarUpload}
              className="hidden"
            />
          </div>
        </motion.div>

        {/* Profile Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="glass rounded-xl p-6 mb-6"
        >
          <h2 className="font-heading text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <User className="w-5 h-5" />
            Profile
          </h2>

          <div className="space-y-4">
            <div>
              <label className="block text-sm text-gray-400 mb-2">Name</label>
              <div className="flex gap-2">
                <Input
                  type="text"
                  value={userName}
                  onChange={(e) => setUserName(e.target.value)}
                  className="input-dark flex-1"
                  placeholder="Your name"
                />
                <Button
                  onClick={handleUpdateName}
                  disabled={savingName || userName === user?.name}
                  className="btn-primary"
                >
                  {savingName ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-2">Email</label>
              <Input
                type="email"
                value={user?.email || ''}
                disabled
                className="input-dark opacity-50 cursor-not-allowed"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-2">Role</label>
              <Input
                type="text"
                value={user?.role || ''}
                disabled
                className="input-dark opacity-50 cursor-not-allowed capitalize"
              />
            </div>
          </div>
        </motion.div>

        {/* Organization Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="glass rounded-xl p-6 mb-6"
        >
          <h2 className="font-heading text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Building className="w-5 h-5" />
            Organization
          </h2>

          {loading ? (
            <div className="py-8 text-center">
              <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-2">Organization Name</label>
                <Input
                  type="text"
                  value={orgName}
                  onChange={(e) => setOrgName(e.target.value)}
                  className="input-dark"
                  data-testid="org-name-input"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">Plan</label>
                <Input
                  type="text"
                  value={organization?.plan || 'activation'}
                  disabled
                  className="input-dark opacity-50 cursor-not-allowed capitalize"
                />
              </div>

              <Button 
                onClick={handleSave}
                className="btn-primary"
                disabled={saving || orgName === organization?.name}
                data-testid="save-settings-btn"
              >
                {saving ? (
                  <Loader className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          )}
        </motion.div>

        {/* Password Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="glass rounded-xl p-6 mb-6"
        >
          <h2 className="font-heading text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Lock className="w-5 h-5" />
            Change Password
          </h2>

          <form onSubmit={async (e) => {
            e.preventDefault();
            
            if (passwordData.newPassword !== passwordData.confirmPassword) {
              toast.error('New passwords do not match');
              return;
            }
            
            if (passwordData.newPassword.length < 6) {
              toast.error('Password must be at least 6 characters');
              return;
            }
            
            setSavingPassword(true);
            try {
              const response = await fetch(`${API_URL}/api/auth/update-password`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                  current_password: passwordData.currentPassword,
                  new_password: passwordData.newPassword
                })
              });
              
              const data = await response.json();
              
              if (response.ok) {
                toast.success('Password updated successfully!');
                setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
              } else {
                toast.error(data.detail || 'Failed to update password');
              }
            } catch (error) {
              toast.error('Failed to update password');
            } finally {
              setSavingPassword(false);
            }
          }} className="space-y-4">
            <div>
              <label className="block text-sm text-gray-400 mb-2">Current Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <Input
                  type={showCurrentPassword ? 'text' : 'password'}
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                  className="input-dark pl-10 pr-10"
                  placeholder="Enter current password"
                  required
                  data-testid="current-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                >
                  {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-2">New Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <Input
                  type={showNewPassword ? 'text' : 'password'}
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                  className="input-dark pl-10 pr-10"
                  placeholder="Enter new password"
                  required
                  minLength={6}
                  data-testid="new-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                >
                  {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">Must be at least 6 characters</p>
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-2">Confirm New Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <Input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                  className="input-dark pl-10 pr-10"
                  placeholder="Confirm new password"
                  required
                  data-testid="confirm-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                >
                  {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {passwordData.confirmPassword && passwordData.newPassword === passwordData.confirmPassword && (
                <p className="text-xs text-green-400 mt-1 flex items-center gap-1">
                  <CheckCircle className="w-3 h-3" /> Passwords match
                </p>
              )}
            </div>

            <Button 
              type="submit"
              className="btn-primary"
              disabled={savingPassword || !passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword}
              data-testid="update-password-btn"
            >
              {savingPassword ? (
                <Loader className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <Lock className="w-4 h-4 mr-2" />
                  Update Password
                </>
              )}
            </Button>
          </form>
        </motion.div>

        {/* Support Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="glass rounded-xl p-6"
        >
          <h2 className="font-heading text-lg font-semibold text-white mb-4">Need Help?</h2>
          
          <div className="space-y-4">
            <a 
              href="mailto:support@aihorsepowered.com"
              className="flex items-center gap-3 p-4 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
            >
              <Mail className="w-5 h-5 text-primary" />
              <div>
                <p className="text-white">Email Support</p>
                <p className="text-sm text-gray-400">support@aihorsepowered.com</p>
              </div>
            </a>

            <a 
              href="tel:8337727397"
              className="flex items-center gap-3 p-4 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
            >
              <Phone className="w-5 h-5 text-primary" />
              <div>
                <p className="text-white">Phone Support</p>
                <p className="text-sm text-gray-400">833-772-7397</p>
              </div>
            </a>

            <a 
              href="https://aihorsepowered.com"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 p-4 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
            >
              <Building className="w-5 h-5 text-primary" />
              <div>
                <p className="text-white">Main Website</p>
                <p className="text-sm text-gray-400">aihorsepowered.com</p>
              </div>
            </a>
          </div>
        </motion.div>
      </div>
    </DashboardLayout>
  );
}
